import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
    collection, 
    onSnapshot, 
    query, 
    where, 
    orderBy, 
    doc, 
    getDoc, 
    getDocs,
    documentId 
} from 'firebase/firestore';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
    UserX, 
    Loader2, 
    CheckCircle, 
    XCircle, 
    AlertTriangle, 
    User, 
    Users, 
    Filter, 
    ChevronDown, 
    Check, 
    Clock 
} from 'lucide-react';

/**
 * Composant unifié pour la consultation des retards et absences.
 */
const MyRetardsAbsences = ({ db, setError, setMessage, schoolId, currentUserId, userRole }) => {
    
    // --- États Partagés ---
    const [absencesRetards, setAbsencesRetards] = useState([]); 
    const [isLoading, setIsLoading] = useState(true); // Gère le chargement des enregistrements (absences/retards)
    const [isResolvingIdentity, setIsResolvingIdentity] = useState(true); // Gère la résolution de l'ID élève/enfants
    
    // --- États Spécifiques Parent ---
    const [children, setChildren] = useState([]); 
    const [selectedChildId, setSelectedChildId] = useState(''); 
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    // --- États Spécifiques Élève ---
    const [studentEleveId, setStudentEleveId] = useState(''); 
    const [studentName, setStudentName] = useState(''); // Ajout de l'état pour le nom de l'élève
    const [studentClassData, setStudentClassData] = useState({ name: 'Chargement...', id: null });
    
    const isParentRole = userRole === 'parent' || userRole === 'superAdmin'; // J'ajoute superAdmin ici car ils peuvent agir comme des parents (voir tout)

    // --- Configuration de l'Année Scolaire Actuelle ---
    const getCurrentSchoolYear = () => {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();
      return currentMonth >= 8 ? `${currentYear}-${currentYear + 1}` : `${currentYear - 1}-${currentYear}`;
    };
    const currentSchoolYear = useMemo(getCurrentSchoolYear, []);

    // --- Fonctions Utilitaires ---
    const formatDateForDisplay = useCallback((date, includeTime = false) => {
        if (!date) return 'N/A';
        const d = date instanceof Date ? date : date.toDate ? date.toDate() : new Date(date);
        return format(d, includeTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy', { locale: fr });
    }, []);

    // ----------------------------------------------------------------------------------
    // --- LOGIQUE PARENT: Récupérer les enfants liés (Eleve IDs) ---
    // ----------------------------------------------------------------------------------
    useEffect(() => {
        if (!currentUserId || !schoolId || !db || !isParentRole) {
            if (isParentRole) setIsResolvingIdentity(false); 
            setChildren([]);
            return;
        }

        const fetchChildrenData = async () => {
            setIsResolvingIdentity(true); 
            try {
                const userDocRef = doc(db, 'users', currentUserId);
                const userDocSnap = await getDoc(userDocRef);

                const linkedEleveIds = userDocSnap.exists() && userDocSnap.data().childrenEleveIds 
                    ? userDocSnap.data().childrenEleveIds.filter(id => id && typeof id === 'string') 
                    : [];
                
                if (linkedEleveIds.length === 0) {
                    setMessage("Aucun enfant n'est actuellement lié à votre compte.");
                    setChildren([]);
                    setIsResolvingIdentity(false); // Ajouté ici
                    return;
                }
                
                const elevesCollectionRef = collection(db, 'schools', schoolId, 'eleves');
                let allChildren = [];
                
                // Logique de batching (optimisation)
                const BATCH_SIZE = 10; 
                const chunks = [];
                for (let i = 0; i < linkedEleveIds.length; i += BATCH_SIZE) {
                    chunks.push(linkedEleveIds.slice(i, i + BATCH_SIZE));
                }

                for (const chunk of chunks) {
                    const elevesQuery = query(elevesCollectionRef, where(documentId(), 'in', chunk));
                    const elevesSnap = await getDocs(elevesQuery);
                    
                    const enrollmentPromises = [];
                    
                    elevesSnap.docs.forEach(eleveDoc => {
                        const eleveData = eleveDoc.data();
                        const eleveId = eleveDoc.id; // C'est l'ID du document Firestore de l'élève
                        const child = {
                            id: eleveId,
                            name: eleveData.name || `${eleveData.firstName || ''} ${eleveData.lastName || ''}`.trim() || eleveId,
                            className: 'Chargement...'
                        };
                        allChildren.push(child);
                        
                        const enrollmentCollectionRef = collection(db, 'schools', schoolId, 'enrollments');
                        const qEnrollment = query(
                            enrollmentCollectionRef,
                            where('studentId', '==', eleveId), // Ici, eleveId est bien l'ID du document Firestore
                            where('schoolYear', '==', currentSchoolYear)
                        );
                        
                        enrollmentPromises.push(
                            getDocs(qEnrollment).then(async snap => {
                                if (!snap.empty) {
                                    const classId = snap.docs[0].data().classId;
                                    if (classId) {
                                        const classDocRef = doc(db, `schools/${schoolId}/classes`, classId);
                                        const classDocSnap = await getDoc(classDocRef);
                                        if (classDocSnap.exists()) {
                                            return { id: eleveId, className: classDocSnap.data().name || classId };
                                        }
                                    }
                                }
                                return { id: eleveId, className: 'Inconnue' };
                            })
                        );
                    });
                    
                    const classResults = await Promise.all(enrollmentPromises);
                    allChildren = allChildren.map(child => {
                        const classInfo = classResults.find(r => r.id === child.id);
                        return { ...child, className: classInfo ? classInfo.className : 'Inconnue' };
                    });
                }
                
                setChildren(allChildren);
                if (allChildren.length > 0) {
                    setSelectedChildId(prevId => prevId || allChildren[0].id);
                }

            } catch (err) {
                console.error("Erreur lors de la récupération des enfants liés (Parent View):", err);
                setError("Erreur lors du chargement des données de vos enfants.");
                setChildren([]);
            } finally {
                setIsResolvingIdentity(false); // FIN DE LA RÉSOLUTION D'IDENTITÉ
            }
        };

        fetchChildrenData();
    }, [db, currentUserId, schoolId, isParentRole, setError, setMessage, currentSchoolYear]);


    // ----------------------------------------------------------------------------------
    // --- LOGIQUE ÉLÈVE: Résoudre l'eleveId (Si !isParentRole) ---
    // ----------------------------------------------------------------------------------
    useEffect(() => {
        if (isParentRole || !currentUserId || !schoolId || !db) {
            if (!isParentRole) setIsResolvingIdentity(false); 
            setStudentEleveId('');
            setStudentName(''); // Réinitialisation du nom
            setStudentClassData({ name: 'Chargement...', id: null });
            return;
        }

        const resolveStudentIdAndClass = async () => {
            setIsResolvingIdentity(true); 
            try {
                let resolvedEleveDocId = '';
                let studentEleveData = null;

                // 1. Trouver l'ID du document Firestore de l'élève en utilisant son Auth UID
                const elevesCollectionRef = collection(db, 'schools', schoolId, 'eleves');
                const eleveQuery = query(elevesCollectionRef, where('authId', '==', currentUserId));
                const eleveSnapshot = await getDocs(eleveQuery);

                if (!eleveSnapshot.empty) {
                    studentEleveData = eleveSnapshot.docs[0].data();
                    resolvedEleveDocId = eleveSnapshot.docs[0].id; // C'est l'ID du document Firestore que nous voulons
                    setStudentName(studentEleveData.name || `${studentEleveData.firstName || ''} ${studentEleveData.lastName || ''}`.trim());
                } else {
                    setError("Impossible de trouver votre profil élève.");
                    setStudentEleveId('');
                    setStudentName('');
                    setStudentClassData({ name: 'Non trouvé', id: null });
                    setIsResolvingIdentity(false);
                    return;
                }

                // 2. Maintenant que nous avons l'ID du document Firestore (resolvedEleveDocId),
                // nous pouvons l'utiliser pour trouver l'inscription et les informations de classe.
                const enrollmentCollectionRef = collection(db, 'schools', schoolId, 'enrollments');
                const activeEnrollmentQuery = query(
                    enrollmentCollectionRef,
                    where('studentId', '==', resolvedEleveDocId), // Utilisation de l'ID du document Firestore
                    where('schoolYear', '==', currentSchoolYear)
                );
                const enrollmentSnapshot = await getDocs(activeEnrollmentQuery);
                
                if (!enrollmentSnapshot.empty) {
                    const enrollmentData = enrollmentSnapshot.docs[0].data();
                    const classeId = enrollmentData.classId;

                    setStudentEleveId(resolvedEleveDocId); // Stocker l'ID du document Firestore ici
                    
                    if (classeId) {
                        const classDocRef = doc(db, `schools/${schoolId}/classes`, classeId);
                        const classDocSnap = await getDoc(classDocRef);
                        setStudentClassData({
                            id: classeId,
                            name: classDocSnap.exists() ? classDocSnap.data().name : 'Classe Inconnue'
                        });
                    } else {
                        setStudentClassData({ name: 'Classe Inconnue', id: null });
                    }
                } else {
                    // Fallback: Si pas d'inscription trouvée pour l'élève (mais le profil élève a été trouvé)
                    setStudentEleveId(resolvedEleveDocId); // On conserve l'ID du document élève même sans inscription active
                    setStudentClassData({ name: 'Non inscrit', id: null });
                    setMessage("Aucune inscription active trouvée pour l'année en cours.");
                }
            } catch (err) {
                console.error("Erreur lors de la résolution de l'ID élève:", err);
                setError("Erreur lors de l'identification de l'élève ou de son inscription.");
                setStudentEleveId('');
                setStudentName('');
            } finally {
                setIsResolvingIdentity(false); // FIN DE LA RÉSOLUTION D'IDENTITÉ
            }
        };

        resolveStudentIdAndClass();
    }, [db, currentUserId, schoolId, isParentRole, setError, setMessage, currentSchoolYear]);


    // ----------------------------------------------------------------------------------
    // --- Écouteur de Données Principal (Absences/Retards) ---
    // ----------------------------------------------------------------------------------
    useEffect(() => {
        // Bloquer le chargement des données tant que l'identité n'est pas résolue
        if (isResolvingIdentity) {
            setIsLoading(true);
            return; 
        }

        let eleveIdToFetch = null;

        if (isParentRole) {
            eleveIdToFetch = selectedChildId;
        } else {
            eleveIdToFetch = studentEleveId;
        }

        // Si l'ID nécessaire est manquant après la résolution, on arrête le chargement.
        if (!db || !schoolId || !eleveIdToFetch) {
            setIsLoading(false); 
            setAbsencesRetards([]);
            return;
        }

        // L'ID est résolu, on peut commencer à charger les données.
        setIsLoading(true);
        setError('');
        setMessage('');

        try {
            const recordsCollectionRef = collection(db, 'schools', schoolId, 'absencesRetards');

            const qRecords = query(
                recordsCollectionRef, 
                where('eleveId', '==', eleveIdToFetch), // Ici, eleveIdToFetch est bien l'ID du document Firestore
                where('schoolYear', '==', currentSchoolYear),
                orderBy("date", "desc")
            );
            
            const unsubscribeRecords = onSnapshot(qRecords, (snapshot) => {
                const recordsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setAbsencesRetards(recordsData);
                setIsLoading(false); 
            }, (error) => {
                setError(`Erreur chargement de votre suivi : ${error.message}`);
                setIsLoading(false); 
            });

            return () => unsubscribeRecords();
        } catch (e) {
            console.error("Erreur de setup Firestore pour le suivi:", e);
            setError("Erreur de configuration de la base de données.");
            setIsLoading(false);
        }
    }, [db, schoolId, isParentRole, selectedChildId, studentEleveId, setError, setMessage, currentSchoolYear, isResolvingIdentity]);


    // --- Détermination de l'entité et des données affichées ---
    const entityToDisplay = useMemo(() => {
        if (isParentRole) {
            const child = children.find(c => c.id === selectedChildId);
            return child || children[0] || null;
        }
        // Pour l'élève, si l'ID a été résolu
        return studentEleveId ? { 
            id: studentEleveId, 
            name: studentName, // Utilise le nom de l'élève stocké dans l'état
            className: studentClassData.name
        } : null;
    }, [isParentRole, children, selectedChildId, studentEleveId, studentName, studentClassData.name]); // Ajout de studentName aux dépendances

    const recordSummary = useMemo(() => {
        const absences = absencesRetards.filter(r => r.type === 'absence').length;
        const retards = absencesRetards.filter(r => r.type === 'retard').length;
        const justified = absencesRetards.filter(r => r.isJustified).length;
        return { total: absencesRetards.length, absences, retards, justified };
    }, [absencesRetards]);


    // ----------------------------------------------------------------------------------
    // --- NOUVEAU BLOC DE RENDU SÉQUENTIEL (FIX) ---
    // ----------------------------------------------------------------------------------
    
    // Étape 1: Afficher un loader pendant la résolution initiale de l'identité
    if (isResolvingIdentity) {
        return (
            <div className="flex justify-center items-center h-40">
                <Loader2 className="animate-spin text-indigo-500" size={32} />
                <p className="ml-3 text-lg text-gray-700">
                    {isParentRole ? "Recherche des enfants liés..." : "Identification de votre compte élève..."}
                </p>
            </div>
        );
    }
    
    // Étape 2: Déterminer si l'identité est résolue APRÈS la résolution (isResolvingIdentity est false ici)
    const isEntityResolved = isParentRole ? children.length > 0 : !!studentEleveId;
    
    // Si la résolution a échoué (pas d'enfant pour le parent, pas d'ID d'élève résolu)
    if (!isEntityResolved) {
         // Cas 2a: Parent sans enfant lié
         if (isParentRole && children.length === 0) {
            return (
                <div className="p-10 text-center bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-xl shadow-lg max-w-4xl mx-auto">
                    <Users className="w-8 h-8 mx-auto mb-3" />
                    <p className="text-lg font-semibold">Aucun Enfant Lié</p>
                    <p>Veuillez contacter l'administration pour lier vos enfants à votre compte afin de consulter leur suivi.</p>
                </div>
            );
        }
        // Cas 2b: Élève non inscrit ou non identifié après résolution
         return (
            <div className="p-10 text-center bg-red-50 border border-red-200 text-red-700 rounded-xl shadow-lg max-w-4xl mx-auto">
                <UserX className="w-8 h-8 mx-auto mb-3" />
                <p className="text-lg font-semibold">Erreur d'Identification</p>
                <p>Impossible d'identifier l'élève ou l'enfant sélectionné. Vérifiez votre profil ou contactez l'administration.</p>
            </div>
        );
    }
    
    // Étape 3: Afficher le loader pendant la récupération des enregistrements (Si l'identité est résolue et qu'on attend les données)
    if (isLoading) {
         return (
            <div className="flex justify-center items-center h-40">
                <Loader2 className="animate-spin text-indigo-500" size={32} />
                <p className="ml-3 text-lg text-gray-700">
                    Chargement de l'historique de suivi de {entityToDisplay?.name || "l'élève sélectionné"}...
                </p>
            </div>
        );
    }


    // Étape 4: Affichage final du composant
    return (
        <div className="p-4 sm:p-6 space-y-6 max-w-6xl mx-auto bg-white rounded-2xl shadow-2xl">
            
            {/* TITRE ET SÉLECTION (POUR PARENT) */}
            <div className="border-b pb-4 flex flex-col md:flex-row items-start md:items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center mb-4 md:mb-0">
                    <UserX size={24} className="mr-3 text-red-600" /> 
                    {isParentRole ? "Suivi des Retards et Absences des Enfants" : "Mon Suivi des Retards et Absences"} 
                    <span className="ml-3 text-sm font-medium text-gray-500"> (Année {currentSchoolYear})</span>
                </h2>
                
                {/* Sélecteur d'enfant (UNIQUEMENT POUR PARENT) */}
                {isParentRole && children.length > 0 && (
                     <div className="relative w-full md:w-64 z-10">
                        <button
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className="w-full text-left bg-red-50 border border-red-300 rounded-xl p-3 flex items-center justify-between shadow-sm hover:border-red-500 transition duration-150"
                            title="Sélectionner un enfant"
                        >
                            <div className='flex items-center'>
                                <Filter className="w-5 h-5 mr-2 text-red-600" />
                                {entityToDisplay ? (
                                    <span className="font-semibold text-gray-800">
                                        {entityToDisplay.name}
                                    </span>
                                ) : (
                                    <span className="text-gray-500">Choisir un enfant...</span>
                                )}
                            </div>
                            <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isDropdownOpen ? 'rotate-180' : 'rotate-0'}`} />
                        </button>

                        {isDropdownOpen && (
                            <div className="absolute w-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 max-h-60 overflow-y-auto">
                                {children.map(child => (
                                    <div
                                        key={child.id}
                                        onClick={() => {
                                            setSelectedChildId(child.id);
                                            setIsDropdownOpen(false);
                                        }}
                                        className={`p-3 cursor-pointer hover:bg-red-100 flex items-center justify-between transition duration-150 ${selectedChildId === child.id ? 'bg-red-200 font-bold' : ''}`}
                                    >
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">{child.name}</p>
                                            <p className="text-xs text-gray-500">Classe: {child.className}</p>
                                        </div>
                                        {selectedChildId === child.id && <Check className="w-4 h-4 text-red-600" />}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
            
            {entityToDisplay && (
                <>
                    {/* Carte de Résumé Statistique */}
                    <div className="grid md:grid-cols-4 gap-4">
                        
                        {/* Entité Actuelle */}
                        <div className="md:col-span-1 p-4 bg-gray-50 border border-gray-200 rounded-xl shadow-md flex items-center">
                            <User size={24} className="text-gray-500 mr-3" />
                            <div>
                                <p className="text-xs font-medium text-gray-500">{isParentRole ? "Enfant Sélectionné" : "Votre Classe"}</p>
                                <span className="text-sm font-semibold text-gray-700">{entityToDisplay.className}</span>
                            </div>
                        </div>
                        
                        {/* Total Absences */}
                        <div className="p-4 bg-red-50 border border-red-200 rounded-xl shadow-md flex items-center">
                            <UserX size={24} className="text-red-600 mr-3" />
                            <div>
                                <p className="text-xs font-medium text-red-700">Absences Total</p>
                                <span className="text-2xl font-bold text-red-600">{recordSummary.absences}</span>
                            </div>
                        </div>

                        {/* Total Retards */}
                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl shadow-md flex items-center">
                            <Clock size={24} className="text-yellow-600 mr-3" />
                            <div>
                                <p className="text-xs font-medium text-yellow-700">Retards Total</p>
                                <span className="text-2xl font-bold text-yellow-600">{recordSummary.retards}</span>
                            </div>
                        </div>

                        {/* Justifiés */}
                        <div className="p-4 bg-green-50 border border-green-200 rounded-xl shadow-md flex items-center">
                            <CheckCircle size={24} className="text-green-600 mr-3" />
                            <div>
                                <p className="text-xs font-medium text-green-700">Total Justifié</p>
                                <span className="text-2xl font-bold text-green-600">{recordSummary.justified}</span>
                            </div>
                        </div>
                    </div>
                    
                    {/* Tableau des enregistrements */}
                    <div className="bg-white shadow-xl rounded-xl overflow-hidden border border-gray-100">
                
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date du Fait</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Justification</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action Administ.</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Classe (Enr.)</th>
                                    </tr>
                                </thead>
                                
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {absencesRetards.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-8 text-center text-sm text-gray-500 italic">
                                                {isParentRole ? 
                                                    `Félicitations ! Aucun retard ou absence enregistré pour ${entityToDisplay.name} cette année.` :
                                                    "Félicitations ! Aucun retard ou absence enregistré pour vous cette année."
                                                }
                                            </td>
                                        </tr>
                                    ) : (
                                        absencesRetards.map(record => (
                                            <tr 
                                                key={record.id} 
                                                className={record.type === 'absence' ? 'bg-red-50 hover:bg-red-100' : 'bg-yellow-50 hover:bg-yellow-100'}
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                        record.type === 'absence' ? 'bg-red-200 text-red-800' : 'bg-yellow-200 text-yellow-800'
                                                    }`}>
                                                        {record.type.toUpperCase()}
                                                    </span>
                                                </td>
                                                
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                    {formatDateForDisplay(record.date)}
                                                    {record.heure && <span className="block font-semibold text-xs text-gray-500">à {record.heure}</span>}
                                                </td>
                                                
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                    {record.isJustified ? (
                                                        <span className="flex items-center text-green-600">
                                                            <CheckCircle size={16} className="mr-1" /> Justifié
                                                        </span>
                                                    ) : (
                                                        <span className="flex items-center text-red-600">
                                                            <XCircle size={16} className="mr-1" /> Non Justifié
                                                        </span>
                                                    )}
                                                </td>

                                                <td className="px-6 py-4 text-sm text-gray-600 max-w-xs break-words">
                                                    {record.justification || 'Pas de justification fournie.'}
                                                </td>
                                                
                                                <td className="px-6 py-4 text-sm text-gray-700 max-w-xs break-words">
                                                    {record.actionEntreprise || 'Aucune action enregistrée par l\'administration.'}
                                                </td>
                                                
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-700">
                                                    {record.className || 'N/A'}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
            
            <p className='text-xs text-gray-400 p-2 border-t mt-4'>
                **Note technique:** Ce composant s'adapte à votre rôle. 
                {isParentRole ? 
                    `Vous consultez les enregistrements de l'enfant sélectionné (${entityToDisplay?.id || 'N/A'}) parmi les ${children.length} enfants liés à votre compte. La requête Firestore utilise l'ID élève (eleveId) de l'enfant.` :
                    `Vous consultez vos propres enregistrements en utilisant votre ID élève résolu (${studentEleveId || 'N/A'}) et votre nom (${studentName || 'N/A'}). La résolution de votre profil élève a été effectuée en cherchant le document 'eleves' avec votre UID d'authentification.`
                }
            </p>
        </div>
    );
};

export default MyRetardsAbsences;
